import type { LocalStop } from './localdb.js';

/**
 * Saying things the way they were promised.
 *
 * The formatting rules here are not cosmetic. Rendering a booking's `startsAt` when the
 * customer was told "mercredi AM" invents a precision nobody gave, and a technician made late
 * by a timestamp nobody promised is a design failure rather than a lateness.
 */

export type Locale = 'fr' | 'en';

/** A `Translatable` from the database, in the language asked for. */
export function text(value: unknown, locale: Locale = 'fr'): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const record = value as Record<string, string | undefined>;
    return record[locale] ?? record.fr ?? record.en ?? '';
  }
  return '';
}

const PERIODS: Record<string, { fr: string; en: string }> = {
  am: { fr: 'avant-midi', en: 'morning' },
  pm: { fr: 'après-midi', en: 'afternoon' },
  day: { fr: 'journée', en: 'all day' },
  evening: { fr: 'en soirée', en: 'evening' },
  week: { fr: 'cette semaine', en: 'this week' },
};

/**
 * The window, read through its precision.
 *
 * `exact` is a time slot. `period` means a half-day was promised and says which. `day` means a
 * date and nothing finer. 539 of the imported bookings are the second kind and 1 726 the
 * first, so this is a real branch and not a defensive one.
 */
export function windowLabel(stop: Pick<LocalStop, 'startsAt' | 'endsAt' | 'precision' | 'period'>, locale: Locale = 'fr'): string {
  if (!stop.startsAt) return '—';
  const start = new Date(stop.startsAt);

  if (stop.precision === 'period') {
    const period = stop.period ? PERIODS[stop.period]?.[locale] : null;
    return period ?? (locale === 'fr' ? 'dans la journée' : 'during the day');
  }
  if (stop.precision === 'day') {
    return locale === 'fr' ? 'journée' : 'all day';
  }

  const from = start.toLocaleTimeString(locale === 'fr' ? 'fr-CA' : 'en-CA', { hour: '2-digit', minute: '2-digit' });
  if (!stop.endsAt) return from;
  const to = new Date(stop.endsAt).toLocaleTimeString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${from} – ${to}`;
}

/** The bit that goes in the time column: a clock only when there is one to give. */
export function windowShort(stop: Pick<LocalStop, 'startsAt' | 'precision' | 'period'>, locale: Locale = 'fr'): string {
  if (!stop.startsAt) return '—';
  if (stop.precision === 'exact') {
    return new Date(stop.startsAt).toLocaleTimeString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  if (stop.precision === 'period' && stop.period) {
    return stop.period === 'am' ? 'AM' : stop.period === 'pm' ? 'PM' : PERIODS[stop.period]?.[locale] ?? '—';
  }
  return locale === 'fr' ? 'jour' : 'day';
}

export function shortTime(value: string | Date | null, locale: Locale = 'fr'): string {
  if (!value) return '—';
  return new Date(value).toLocaleTimeString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function dayLabel(value: string | Date, locale: Locale = 'fr'): string {
  return new Date(value).toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** A date without a time, for a history entry that is years old. */
export function dateLabel(value: string | Date | null, locale: Locale = 'fr'): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Money, from the string the server sent. Never parsed into a float for display. */
export function amount(value: string | null | undefined, locale: Locale = 'fr'): string {
  const number = Number(value ?? '0');
  return new Intl.NumberFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(Number.isFinite(number) ? number : 0);
}

export function minutes(total: number, locale: Locale = 'fr'): string {
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (!hours) return `${rest} min`;
  return locale === 'fr' ? `${hours} h ${String(rest).padStart(2, '0')}` : `${hours}h ${rest}m`;
}

/**
 * The address a technician reads at the door.
 *
 * The unit comes first and in full, because 614 imported addresses carry it inside `line1` as
 * `207-300 Rue Fernand-Casavant` — and on a 63-unit building, "300 Rue Fernand-Casavant" alone
 * is not an address.
 */
export function addressLines(address: LocalStop['address']): { primary: string; secondary: string } {
  if (!address) return { primary: '—', secondary: '' };
  const primary = address.unit && !address.line1.startsWith(`${address.unit}-`)
    ? `${address.unit} — ${address.line1}`
    : address.line1;
  return { primary, secondary: [address.line2, address.city].filter(Boolean).join(', ') };
}

/** A `geo:` link the phone's own map app answers. No routing engine, just a handoff. */
export function mapHref(address: LocalStop['address']): string | null {
  if (!address) return null;
  const query = encodeURIComponent(`${address.line1}, ${address.city}`);
  return address.latitude != null && address.longitude != null
    ? `geo:${address.latitude},${address.longitude}?q=${query}`
    : `geo:0,0?q=${query}`;
}
